// @ts-strict-ignore
import React from 'react'
import { capitalize, isEmpty, startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, Button, DataListItem, Icon } from '@node-space/ui-components'
import { CustomerDocumentStatusHighlight } from 'pages/Customers/components/CustomerHighlight'
import type { CustomerDocument } from 'pages/EmbeddedCustomers/helpers/types'

type ListDocument = CustomerDocument & { isUploaded?: boolean }
interface CustomerDocumentsListProps {
  listDocuments: ListDocument[]
  sectionTitle?: string
  isCompany: boolean
  requiredDocName?: Record<string, string>
  onDocumentLink: (document: CustomerDocument) => void
}

export const CustomerDocumentsList = ({
  listDocuments,
  sectionTitle,
  isCompany,
  requiredDocName,
  onDocumentLink,
}: CustomerDocumentsListProps) => {
  const { t } = useTranslation()

  const getDisplayName = (document: CustomerDocument) => {
    if (isCompany) {
      const key = document?.subType ?? document?.type ?? 'OTHER'
      return startCase(key.toLowerCase().replaceAll('_', ' '))
    }

    const docType = document?.type
    if (docType === 'OTHER') return t('embeddedCustomers.requiredDocuments.other')
    return requiredDocName?.[docType] ?? requiredDocName?.[document?.subType] ?? docType ?? ''
  }

  const getDocumentElement = (document: ListDocument) => {
    if (!document?.isUploaded) return null
    if (isCompany) {
      return (
        <Button
          testId="download-document"
          iconElement={<Icon name="DownloadSimple" />}
          onClick={() => onDocumentLink(document)}
          variant="ghost"
        >
          {document?.name}
        </Button>
      )
    }

    return (
      <Box className="flex">
        <span className="text-body-regular text-foreground-subdued ml-3">{document?.name}</span>
      </Box>
    )
  }

  if (isEmpty(listDocuments)) return null

  return (
    <Box className="flex flex-col gap-2">
      {sectionTitle && (
        <span className="text-body-semibold text-foreground-subdued">{sectionTitle}</span>
      )}
      {listDocuments?.map((document, idx) => {
        const isUploaded = document?.isUploaded
        const requiredKey = isCompany ? document?.subType : document?.type
        const displayName = getDisplayName(document)
        const rowKey = isUploaded
          ? document?.reference
          : `${requiredKey ?? document?.name ?? 'document'}-${idx}`
        return (
          <Box key={rowKey}>
            <DataListItem
              layout="horizontal"
              body=""
              title={`${idx + 1}. ${startCase(capitalize(displayName))}`}
              id={rowKey}
              testId="customer-document"
              HeaderHighlight={
                <CustomerDocumentStatusHighlight
                  status={document?.status}
                  isUploaded={isUploaded}
                />
              }
            />
            {getDocumentElement(document)}
          </Box>
        )
      })}
    </Box>
  )
}
